import {
  useInfiniteQuery,
  useQuery,
  InfiniteData,
} from '@tanstack/react-query';
import type { ApiResponse } from 'hooks/api/api';
import {
  TCTemplateListResponse,
  TCTemplateKind,
  TCTemplateRecord,
  V1TemplatesService,
} from 'src/api/generated';

type TemplateListQuery = {
  kind?: TCTemplateKind;
  origin?: string;
  tag?: string;
  limit?: number;
};

export const useWorkflowTemplatesList = (
  params?: TemplateListQuery,
  enabled = true
): ApiResponse & {
  templates: TCTemplateRecord[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
} => {
  const fetchTemplates = async ({
    pageParam,
  }: {
    pageParam: unknown;
  }): Promise<TCTemplateListResponse> => {
    const pageToken = pageParam as string | undefined;
    return V1TemplatesService.listTemplates(
      params?.limit,
      pageToken,
      params?.kind,
      params?.origin,
      params?.tag
    );
  };

  const getNextPageParam = (lastPage: TCTemplateListResponse) =>
    lastPage.nextPageToken as string | undefined;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<
    TCTemplateListResponse,
    Error,
    InfiniteData<TCTemplateListResponse>
  >({
    queryKey: ['templates', params],
    queryFn: fetchTemplates,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const templates = ((data?.pages ?? []).flatMap((page) => page.items) ??
    []) as unknown as TCTemplateRecord[];

  return {
    loading,
    error: error || null,
    templates,
    refetch,
    fetchNextPage,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
  };
};

export const useWorkflowTemplate = (
  templateId?: string,
  enabled = true
): ApiResponse & { template: TCTemplateRecord | null } => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['templates', templateId],
    queryFn: async () => {
      if (!templateId) return null;
      return V1TemplatesService.getTemplate(templateId);
    },
    enabled: enabled && Boolean(templateId),
    staleTime: 15_000,
  });

  return {
    loading,
    error: error || null,
    template: data ?? null,
    refetch,
  };
};
